import { FunctionComponent } from 'react'
import styles from './parentHome.module.scss'
import Title from '@/components/_custom/Title/title'
import ProfileIcon from '@mui/icons-material/AccountCircle'
import { ChildCareOutlined, LockReset, MailOutline, Notifications } from '@mui/icons-material'
import TranslateIcon from '@mui/icons-material/Translate'
import LogoutIcon from '@mui/icons-material/ExitToApp'
import ArticleIcon from '@mui/icons-material/Article'
import HelpIcon from '@mui/icons-material/Help'
import CachedIcon from '@mui/icons-material/Cached'
import { removeServiceWorker } from '@/modules/utils/serviceWorker'
import { useTranslation } from 'react-i18next'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { useAuth } from '@/modules/context/AuthContext'
import HomeCase from '../HomeCase/homeCase'
import { DaycareGroupsUrl } from '@/types/DaycareGroup'
import { getDaycareGroupsIconTextToJSX } from '@/modules/helper/iconsHelper'
import { useDaycareMailTo } from '@/hooks/useDaycareMailTo'

interface ParentHomeDesktopProps {
    firstname: string
    backendVersion: string
    frontendVersion: string
    daycaresUrls: DaycareGroupsUrl[]
    setDialogChangeLanguageOpen: (open: boolean) => void
    setDialogForgottenPasswordOpen: (open: boolean) => void
    setDialogSupportOpen: (open: boolean) => void
}

const ParentHomeDesktop: FunctionComponent<ParentHomeDesktopProps> = ({
    firstname,
    backendVersion,
    frontendVersion,
    daycaresUrls,
    setDialogChangeLanguageOpen,
    setDialogForgottenPasswordOpen,
    setDialogSupportOpen
}: ParentHomeDesktopProps) => {
    const { t } = useTranslation()

    const navigate: NavigateFunction = useNavigate()

    const { canContactDaycare, contactDaycare } = useDaycareMailTo()
    const { user, logout } = useAuth()

    return (
        <div className={styles.PagePosition}>
            <div className={styles.HomePlacement}>
                <Title text={`${t('hello')} ${firstname}`} className={styles.Title} />
                <div className={styles.CaseContainer}>
                    <HomeCase
                        text={t('myProfile')}
                        icon={<ProfileIcon className={styles.CaseIcon} />}
                        onClick={(): void | Promise<void> => navigate('/account/profile')}
                        isParent={true}
                    />
                    <HomeCase
                        text={t('myChilds')}
                        icon={<ChildCareOutlined className={styles.CaseIcon} />}
                        onClick={(): void | Promise<void> => navigate('/account/childs')}
                        isParent={true}
                    />
                    <HomeCase
                        text={t('myAlerts')}
                        icon={<Notifications className={styles.CaseIcon} />}
                        onClick={(): void | Promise<void> => navigate('/account/notifications')}
                        isParent={true}
                    />
                    {canContactDaycare && (
                        <HomeCase
                            text={t('contactMyDaycare')}
                            icon={<MailOutline className={styles.CaseIcon} />}
                            onClick={(): void | Promise<void> => contactDaycare()}
                            isParent={true}
                        />
                    )}
                </div>
            </div>
            {daycaresUrls.length > 0 && (
                <div className={styles.HomePlacement}>
                    <Title
                        className={styles.Title}
                        text={user && user.daycares.length > 1 ? t('myDaycaresUrls') : t('myDaycareUrls')}
                    />
                    <div className={styles.CustomCaseContainer}>
                        {daycaresUrls.map((daycareUrl: DaycareGroupsUrl, index: number) => (
                            <HomeCase
                                key={index}
                                text={daycareUrl.text}
                                icon={getDaycareGroupsIconTextToJSX(daycareUrl.icon, styles.CaseIcon)}
                                href={daycareUrl.url}
                                isParent={true}
                                className={styles.CustomCase}
                            />
                        ))}
                    </div>
                </div>
            )}
            <div className={styles.HomePlacement}>
                <Title text={t('settings')} className={styles.Title} />
                <div className={styles.CaseContainer}>
                    <HomeCase
                        text={t('language')}
                        icon={<TranslateIcon className={styles.CaseIcon} />}
                        onClick={(): void | Promise<void> => setDialogChangeLanguageOpen(true)}
                        isParent={true}
                    />
                    <HomeCase
                        text={t('password')}
                        icon={<LockReset className={styles.CaseIcon} />}
                        onClick={(): void | Promise<void> => setDialogForgottenPasswordOpen(true)}
                        isParent={true}
                    />
                    <HomeCase
                        text={t('logout')}
                        icon={<LogoutIcon className={styles.CaseIcon} />}
                        onClick={logout}
                        isParent={true}
                    />
                </div>
                <div className={styles.CaseContainer}>
                    <HomeCase
                        text={t('ourArticles')}
                        icon={<ArticleIcon className={styles.CaseIcon} />}
                        href={'https://www.cald.fr/nos-articles/'}
                        isParent={true}
                    />
                    <HomeCase
                        text={t('help')}
                        icon={<HelpIcon className={styles.CaseIcon} />}
                        href={'https://www.cald.fr/tutoriel-utilisateur'}
                        isParent={true}
                    />
                    <HomeCase
                        text={t('refresh')}
                        icon={<CachedIcon className={styles.CaseIcon} />}
                        onClick={removeServiceWorker}
                        isParent={true}
                    />
                </div>
            </div>
            <div className={styles.Version}>
                <a onClick={() => setDialogSupportOpen(true)}>
                    V{backendVersion}/{frontendVersion}
                </a>
            </div>
        </div>
    )
}

export default ParentHomeDesktop
